import React, { useState, useRef } from "react";
import { MoreVertical } from "lucide-react";
import { PopoverMenu, MenuItem } from "./PopoverMenu";

export interface EllipsisMenuProps {
  items: MenuItem[];
  /**
   * Optional custom trigger button
   * If not provided, defaults to a MoreVertical icon button
   */
  trigger?: React.ReactNode;
  /**
   * Optional className for the trigger button
   */
  triggerClassName?: string;
}

/**
 * Ellipsis menu for button-triggered dropdown menus
 * Uses PopoverMenu with "bottom-right" alignment
 */
export function EllipsisMenu({
  items,
  trigger,
  triggerClassName = "",
}: EllipsisMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        x: rect.right,
        y: rect.bottom,
      });
    }

    setIsOpen(!isOpen);
  };

  return (
    <>
      <button
        ref={triggerRef}
        onClick={handleClick}
        className={
          triggerClassName ||
          "rounded-lg p-1.5 text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
        }
        type="button"
        aria-label="More options"
      >
        {trigger || <MoreVertical className="h-4 w-4" />}
      </button>

      {isOpen && (
        <PopoverMenu
          x={menuPosition.x}
          y={menuPosition.y}
          items={items}
          onClose={() => setIsOpen(false)}
          align="bottom-right"
        />
      )}
    </>
  );
}
